import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import RiskRing from "@/components/RiskRing";
import ActivityTimeline from "@/components/ActivityTimeline";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { useAnimatedNumber } from "@/lib/useAnimatedNumber";
import { Buildings, ClipboardText, ArrowRight, Sparkle, TrendDown, CheckCircle, Clock } from "@phosphor-icons/react";

function AnimatedKPI({ label, value, sub, tone, icon, delay }) {
  const v = useAnimatedNumber(value, 1000);
  return (
    <div className={`bg-white border border-slate-200 rounded-md p-5 lift-on-hover fade-up fade-up-${delay}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`text-3xl font-semibold mt-1 ${tone || "text-slate-900"}`} style={{ fontFamily: "Geist" }}>
        {Math.round(v)}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AuditDashboard() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [observations, setObservations] = useState([]);
  const [insights, setInsights] = useState(null);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshInsights = async () => {
    if (!companies.length) return;
    setInsightsRefreshing(true);
    try {
      const r = await api.get(`/analytics/ai-insights?company_id=${companies[0].id}&refresh=true`);
      setInsights(r.data);
    } finally {
      setInsightsRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get("/companies");
        setCompanies(c.data);
        const allObs = [];
        for (const co of c.data) {
          const r = await api.get(`/observations?company_id=${co.id}`);
          allObs.push(...r.data);
        }
        setObservations(allObs);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!companies.length) return;
      try {
        const all = await Promise.all(
          companies.map((c) => api.get(`/analytics/risk-matrix?company_id=${c.id}`).then((r) => r.data))
        );
        const agg = { High: 0, Medium: 0, Low: 0 };
        let inherent = 0;
        let residual = 0;
        let total = 0;
        all.forEach((m) => {
          agg.High += m.counts_by_risk.High || 0;
          agg.Medium += m.counts_by_risk.Medium || 0;
          agg.Low += m.counts_by_risk.Low || 0;
          inherent += (m.inherent_risk_score || 0) * (m.total || 0);
          residual += (m.residual_risk_score || 0) * (m.total || 0);
          total += m.total || 0;
        });
        setMetrics({
          counts_by_risk: agg,
          inherent_risk_score: total ? Math.round((inherent / total) * 10) / 10 : 0,
          residual_risk_score: total ? Math.round((residual / total) * 10) / 10 : 0,
          reduction_pct: total && inherent ? Math.round(((inherent - residual) / inherent) * 1000) / 10 : 0,
          total,
        });
      } catch {
        // analytics best-effort
      }
    })();
  }, [companies]);

  useEffect(() => {
    (async () => {
      if (!companies.length) return;
      try {
        const r = await api.get(`/analytics/ai-insights?company_id=${companies[0].id}`);
        setInsights(r.data);
      } catch {
        // best-effort
      }
    })();
  }, [companies]);

  const totalOpen = observations.filter((o) => o.status === "Open" || o.status === "In-Progress").length;
  const totalResolved = observations.filter((o) => o.status === "Resolved" || o.status === "Closed").length;
  const completion = observations.length ? Math.round((totalResolved / observations.length) * 100) : 0;
  const animatedCompletion = useAnimatedNumber(completion, 1200);

  const header = (
    <div className="fade-up">
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[#0B2A5B] text-[11px] mb-1 font-semibold">
        <Sparkle size={11} weight="fill" /> AI-Powered Audit Console
      </div>
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>
        Welcome back, {user?.name?.split(" ")[0] || "Auditor"}
      </h1>
      <p className="text-sm text-slate-500 mt-0.5">Here's your firm-wide risk posture and what changed recently.</p>
    </div>
  );

  return (
    <AppLayout header={header}>
      {/* Hero risk panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Big risk ring */}
        <div className="bg-gradient-to-br from-white to-blue-50/40 border border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center pop-in" data-testid="hero-risk-ring">
          <RiskRing
            score={metrics?.residual_risk_score || 0}
            inherent={metrics?.inherent_risk_score || 0}
            reduction={metrics?.reduction_pct || 0}
            size={200}
          />
        </div>

        {/* Completion + counts */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#0B2A5B] text-white border border-[#0B2A5B] rounded-lg p-6 relative overflow-hidden fade-up fade-up-1">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#A8B86C]/10 rounded-full -mr-12 -mt-12"></div>
            <div className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold flex items-center gap-1.5">
              <TrendDown size={11} weight="fill" /> Cycle Completion
            </div>
            <div className="flex items-end gap-3 mt-2">
              <div className="text-5xl font-bold" style={{ fontFamily: "Geist" }}>
                {Math.round(animatedCompletion)}<span className="text-2xl text-blue-200">%</span>
              </div>
              <div className="text-sm text-blue-200 pb-2">{totalResolved} of {observations.length} resolved</div>
            </div>
            <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#A8B86C] rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${animatedCompletion}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AnimatedKPI label="Clients" value={companies.length} icon={<Buildings size={11} />} delay={2} />
            <AnimatedKPI label="Total Findings" value={observations.length} icon={<ClipboardText size={11} />} delay={3} />
            <AnimatedKPI label="In Flight" value={totalOpen} tone="text-amber-700" icon={<Clock size={11} />} sub="Open or in-progress" delay={4} />
            <AnimatedKPI label="Resolved" value={totalResolved} tone="text-emerald-700" icon={<CheckCircle size={11} />} sub="Closed or accepted" delay={5} />
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="mb-6 fade-up fade-up-3">
        <AIInsightsPanel insights={insights} loading={!insights && companies.length > 0} metrics={metrics} onRefresh={refreshInsights} refreshing={insightsRefreshing} />
      </div>

      {/* Two-column: portfolio + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-5 fade-up fade-up-4" data-testid="portfolio-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Portfolio</div>
              <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Geist" }}>Your Clients</h3>
            </div>
            <Link to="/audit/companies" className="text-xs text-blue-600 hover:underline">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {companies.slice(0, 6).map((c, i) => {
              const co_obs = observations.filter((o) => o.company_id === c.id);
              const open = co_obs.filter((o) => o.status === "Open" || o.status === "In-Progress").length;
              const done = co_obs.filter((o) => o.status === "Resolved" || o.status === "Closed").length;
              const pct = co_obs.length ? Math.round((done / co_obs.length) * 100) : 0;
              return (
                <Link
                  key={c.id}
                  to={`/audit/companies/${c.id}`}
                  className={`flex items-center gap-4 py-3 hover:bg-slate-50 -mx-2 px-2 rounded transition fade-up fade-up-${Math.min(6, i + 1)}`}
                  data-testid={`dashboard-company-row-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-[#0B2A5B] text-white flex items-center justify-center shrink-0">
                    <Buildings size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.industry || "—"} · {co_obs.length} findings</div>
                  </div>
                  <div className="hidden md:flex flex-col items-end shrink-0 w-32">
                    <div className="flex items-center justify-between w-full text-[10px] text-slate-500 mb-1">
                      <span>{pct}%</span>
                      <span className="text-amber-700">{open} open</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-slate-400 shrink-0" />
                </Link>
              );
            })}
            {companies.length === 0 && !loading && (
              <Link
                to="/audit/companies"
                className="block py-10 text-center border border-dashed border-slate-300 rounded hover:border-blue-400 transition"
              >
                <Buildings size={28} className="mx-auto text-slate-400" />
                <div className="text-sm text-slate-700 font-medium mt-2">Onboard your first client</div>
                <div className="text-xs text-slate-500 mt-1">Add a company to begin tracking observations →</div>
              </Link>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5 fade-up fade-up-5" data-testid="recent-activity-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Live Feed</div>
              <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Geist" }}>Recent Activity</h3>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> Live
            </span>
          </div>
          {companies[0]?.id ? (
            <ActivityTimeline companyId={companies[0].id} compact />
          ) : (
            <div className="text-sm text-slate-500 text-center py-8">Activity will appear here after you create a client.</div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
